import { Component, EventEmitter, forwardRef, Input, OnInit, Output } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MomentDateAdapter } from '@angular/material-moment-adapter';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { Masks } from '@core/custom-validators/masks';

// Adapter para sobrescrever nomes de dias
class CustomDateAdapter extends MomentDateAdapter {
  override getDayOfWeekNames(style: 'long' | 'short' | 'narrow') {
    return ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sab'];
  }
}

// Formato customizado (Data + Hora)
export const MATERIAL_DATEPICKER_FORMATS = {
  parse: {
    dateInput: 'DD/MM/YYYY',
  },
  display: {
    dateInput: 'DD/MM/YYYY',
    monthYearLabel: 'MMMM YYYY',
    dateA11yLabel: 'DD/MM/YYYY',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

@Component({
  selector: 'app-datetime-picker',
  templateUrl: './datetime-picker.component.html',
  styleUrls: ['./datetime-picker.component.scss'],
  providers: [
    {
      provide: DateAdapter,
      useClass: CustomDateAdapter,
      deps: [MAT_DATE_LOCALE],
    },
    { provide: MAT_DATE_FORMATS, useValue: MATERIAL_DATEPICKER_FORMATS },
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DatetimePickerComponent),
      multi: true,
    },
  ],
})

export class DatetimePickerComponent implements ControlValueAccessor {
  @Input() placeholder: string = '';
  @Input() name: string = '';
  @Input() id: string = '';
  @Input() label: string = '';
  @Input() todayAsMaxDate: any = '';
  @Input() readonly: boolean = false;
  @Input() opcional: any;
  @Input() required: boolean = false;
  @Input() showTime: boolean = false;
  @Input() disabled: boolean = false;
  @Input() isModal: boolean;
  value: Date | null = null;
  wasInputFocused = false;
  wrongInput = false;
  dateInputValue: Date | null = null;
  timeValue: string = '';

  private onChange: (value: any) => void = () => { };
  private onTouched: () => void = () => { };

  dateMask = {
    mask: Masks.data,
    guide: false,
    modelClean: true
  };

  writeValue(value: any): void {
    if (!value) {
      this.dateInputValue = null;
      this.timeValue = '';
      return;
    }

    if (typeof value === 'string' && value.includes('T')) {
      const [datePart, timePart] = value.split('T');
      const [year, month, day] = datePart.split('-').map(Number);
      const [hour, minute] = timePart.split(':').map(Number);

      this.dateInputValue = new Date(year, month - 1, day, hour, minute);

      if (this.showTime) {
        this.timeValue = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
      }
    } else {
      this.dateInputValue = value;
    }
  }


  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  setInputValue(value: string) {
    this.dateInputValue = value ? new Date(value) : null;
    this.updateValue();
  }

  validateFormat(dateValue: Date) {
    this.wrongInput = !dateValue;
  }

  emitDate(event: any) {
    this.dateInputValue = event?.value?._d ?? this.dateInputValue;
    this.updateValue();
  }

  updateValue() {
    let result: Date | null = this.dateInputValue;

    if (result && this.showTime && this.timeValue) {
      const [hours, minutes] = this.timeValue.split(':').map(Number);
      result.setHours(hours, minutes, 0, 0);
    }

    let formatted: string | null = null;
    if (result instanceof Date && !isNaN(result.getTime())) {
      const yyyy = result.getFullYear();
      const MM = String(result.getMonth() + 1).padStart(2, '0');
      const dd = String(result.getDate()).padStart(2, '0');
      const HH = String(result.getHours()).padStart(2, '0');
      const mm = String(result.getMinutes()).padStart(2, '0');
      const ss = String(result.getSeconds()).padStart(2, '0');
      
      this.isModal ? formatted = `${yyyy}-${MM}-${dd}T${HH}:${mm}:${ss}` : formatted = `${yyyy}-${MM}-${dd}T00:00:00`
    }

    this.onChange(formatted);
    this.onTouched();
  }

  clearDate() {
    this.dateInputValue = null;
    this.timeValue = '';
    this.updateValue();
  }
}
